import { readApiError } from '@/shared/platform';
import { useState } from 'react';
import { useRcmSession } from '@/features/session/hooks/useRcmSession';
import { usePmsAccess, useStopPmsAccess } from '../hooks/usePmsAccess';
import { STOP_CONFIRMATION } from '../api/pmsAccessApi';
import type { PmsConnection, PmsEntityLag } from '../api/types';

/**
 * Settings › PMS access (P13-09).
 *
 * The one live page in Settings: everything else here is still fixture data.
 * It answers four things in the order a practice asks them — connected, how
 * current, what is held back, how do I stop it — and the fourth is the reason
 * the page exists.
 */
export function PmsAccessBody() {
  const { practice } = useRcmSession();
  const practiceId = practice?.id ?? null;
  const { data, isPending, error } = usePmsAccess(practiceId);

  if (practiceId === null) {
    return (
      <Note tone="warning">
        This location is not set up in RCM yet, so there is nothing to connect.
      </Note>
    );
  }
  if (isPending) {
    return <Note tone="muted">Checking the connection…</Note>;
  }
  if (error) {
    return (
      <Note tone="danger">{readApiError(error)?.message ?? 'No answer from the RCM backend.'}</Note>
    );
  }
  return (
    <div className="mx-auto flex w-full max-w-[920px] flex-col gap-4 px-5 py-5 sm:px-6">
      {data.stopped && (
        <Note tone="warning">
          <strong className="font-semibold">ELVA is paused.</strong>{' '}
          {data.stop_reason || 'ELVA’s team stopped this while it checks something.'}
        </Note>
      )}

      {data.never_connected ? (
        <Note tone="muted">
          No practice management system is connected yet. Once ELVA is connected, this page shows
          how current its reading is.
        </Note>
      ) : (
        data.connections.map((connection) => (
          <ConnectionCard key={connection.pms} connection={connection} />
        ))
      )}

      <StopSwitch practiceId={practiceId} disabled={data.never_connected} />
    </div>
  );
}

/* ---------------------------------------------------------------- */

const READS_STATE_LABEL: Record<PmsConnection['reads_state'], string> = {
  live: 'Reading',
  // Not "Connecting": `validating` is the first sync, which is a real,
  // working state and can last days.
  validating: 'First sync running',
  off: 'Not switched on',
  suspended: 'Paused',
};

function ConnectionCard({ connection }: { connection: PmsConnection }) {
  const { shared_server: shared, data_quality: quality } = connection;
  const dot =
    connection.reads_state === 'live'
      ? 'var(--success)'
      : connection.reads_state === 'validating'
        ? 'var(--info)'
        : 'var(--gold)';

  return (
    <section className="rounded-[14px] border border-[var(--cream-2)] bg-[var(--cream-1)] p-5">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span
          className="inline-block h-[8px] w-[8px] shrink-0 rounded-full"
          style={{ background: dot }}
        />
        <h3 className="text-[15px] font-semibold capitalize text-[var(--ink-0)]">
          {connection.pms}
        </h3>
        <span className="text-[12.5px] text-[var(--ink-2)]">
          {READS_STATE_LABEL[connection.reads_state]}
        </span>
        <span className="ml-auto text-[12.5px] text-[var(--ink-3)]">
          Last read {relative(connection.last_successful_sync_at)}
        </span>
      </header>

      {connection.last_sync_outcome === 'incomplete' && (
        <p className="mt-3 text-[12.5px] leading-relaxed text-[var(--ink-2)]">
          This system does not report deletions, so ELVA compares everything nightly to find
          anything removed in the practice.
        </p>
      )}

      <div className="mt-4 border-t border-[var(--cream-2)] pt-4">
        <Eyebrow>How current</Eyebrow>
        {connection.lag.length === 0 ? (
          <p className="mt-1.5 text-[12.5px] text-[var(--ink-3)]">Nothing has been read yet.</p>
        ) : (
          <ul className="mt-1.5 flex flex-col gap-1">
            {connection.lag.map((entry) => (
              <LagRow key={entry.entity} entry={entry} />
            ))}
          </ul>
        )}
      </div>

      {shared.clinics_on_server !== null && shared.clinics_on_server > 1 && (
        <div className="mt-4 border-t border-[var(--cream-2)] pt-4">
          <Eyebrow>Shared server</Eyebrow>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-[var(--ink-2)]">
            {shared.clinics_on_server} clinics share this system and {shared.clinics_owned}{' '}
            {shared.clinics_owned === 1 ? 'is yours' : 'are yours'}. ELVA reads only yours.{' '}
            {shared.filter_state === 'verified'
              ? 'The filter has been tested.'
              : 'Reading waits until the filter has been tested.'}
          </p>
          {shared.disagrees_with_your_answer && (
            <Note tone="warning" className="mt-2">
              This does not match what you told us about this server. ELVA’s team is checking it
              before reading anything.
            </Note>
          )}
        </div>
      )}

      {(!quality.clear || quality.warnings.length > 0) && (
        <div className="mt-4 border-t border-[var(--cream-2)] pt-4">
          <Eyebrow>Before ELVA can do more</Eyebrow>
          <ul className="mt-1.5 flex flex-col gap-1.5">
            {quality.blockers.map((finding) => (
              <li key={finding.check} className="flex gap-2 text-[12.5px] leading-relaxed">
                <span className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-full bg-[var(--terracotta)]" />
                <span className="text-[var(--ink-1)]">
                  {finding.question}
                  <span className="text-[var(--ink-3)]"> — {finding.count} to fix</span>
                </span>
              </li>
            ))}
            {quality.warnings.map((finding) => (
              <li key={finding.check} className="flex gap-2 text-[12.5px] leading-relaxed">
                <span className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-full bg-[var(--gold)]" />
                <span className="text-[var(--ink-2)]">
                  {finding.question}
                  <span className="text-[var(--ink-3)]"> — {finding.count}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {connection.open_gaps > 0 && (
        <p className="mt-3 text-[12px] text-[var(--ink-3)]">
          {connection.open_gaps} difference{connection.open_gaps === 1 ? '' : 's'} found between
          ELVA and your system, under review.
        </p>
      )}
    </section>
  );
}

function LagRow({ entry }: { entry: PmsEntityLag }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 text-[12.5px]">
      <span className="min-w-[110px] capitalize text-[var(--ink-1)]">
        {entry.entity.replace(/_/g, ' ')}
      </span>
      <span
        className="tabular-nums"
        style={{ color: entry.suspicious ? 'var(--terracotta)' : 'var(--ink-2)' }}
      >
        {behind(entry.seconds_behind)}
      </span>
      {!entry.backfilled && <span className="text-[var(--ink-3)]">still catching up</span>}
      {entry.suspicious && entry.reason && (
        <span className="text-[var(--terracotta)]">{entry.reason}</span>
      )}
    </li>
  );
}

/* ---------------------------------------------------------------- */

/**
 * The stop switch.
 *
 * Terminal, so it asks for the word back rather than putting a destructive
 * action one click away. The backend asks for it too — this is not the only
 * thing standing between a mis-click and a destroyed credential.
 */
function StopSwitch({ practiceId, disabled }: { practiceId: string; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const stop = useStopPmsAccess(practiceId);

  if (stop.isSuccess) {
    const result = stop.data;
    return (
      <Note tone="warning">
        <strong className="font-semibold">ELVA has stopped reading.</strong> Its saved sign-in
        details have been deleted
        {result.terminal
          ? '.'
          : `, and the copy in our secure store is erased for good on ${onDate(result.purges_at)}.`}{' '}
        <strong className="font-semibold">
          Now change the password in your practice management system.
        </strong>{' '}
        That is what stops the sign-in working — until you do, it still would.
      </Note>
    );
  }

  return (
    <section className="rounded-[14px] border border-[var(--terracotta)]/35 bg-[var(--cream-1)] p-5">
      <h3 className="text-[15px] font-semibold text-[var(--ink-0)]">Stop ELVA</h3>
      <p className="mt-1.5 text-[12.5px] leading-relaxed text-[var(--ink-2)]">
        Stops ELVA reading your practice management system and deletes the sign-in details it holds.
        Starting again means entering them afresh.
      </p>
      <p className="mt-2 text-[12.5px] leading-relaxed text-[var(--ink-2)]">
        Afterwards, change the password in your practice management system. Deleting our copy stops
        ELVA; changing the password is what stops the sign-in itself.
      </p>

      {!open ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            setOpen(true);
          }}
          className="mt-3 h-8 rounded-[9px] border border-[var(--cream-2)] px-3 text-[12.5px] font-medium text-[var(--terracotta)] transition-colors hover:bg-[var(--terracotta-tint)] disabled:cursor-not-allowed disabled:text-[var(--ink-4)] disabled:hover:bg-transparent"
        >
          Stop ELVA
        </button>
      ) : (
        <div className="mt-3 flex flex-col gap-2.5">
          <label className="flex flex-col gap-1 text-[12px] text-[var(--ink-2)]">
            Why are you stopping ELVA? (optional, shown in your account history)
            <input
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              className="h-8 rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 text-[12.5px] text-[var(--ink-1)]"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px] text-[var(--ink-2)]">
            Type {STOP_CONFIRMATION} to confirm
            <input
              value={typed}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
              autoComplete="off"
              className="h-8 w-[160px] rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 text-[12.5px] tracking-wide text-[var(--ink-1)]"
            />
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={typed !== STOP_CONFIRMATION || stop.isPending}
              onClick={() => {
                stop.mutate(reason);
              }}
              className="h-8 rounded-[9px] px-3 text-[12.5px] font-medium text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
              style={{ background: 'var(--terracotta)' }}
            >
              {stop.isPending ? 'Stopping…' : 'Stop ELVA and destroy the details'}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setTyped('');
              }}
              className="h-8 rounded-[9px] border border-[var(--cream-2)] px-3 text-[12.5px] font-medium text-[var(--ink-1)] hover:bg-[var(--surface-mute)]"
            >
              Cancel
            </button>
          </div>
          {stop.error && (
            <Note tone="danger">
              {readApiError(stop.error)?.message ?? 'That did not go through.'}
            </Note>
          )}
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- */

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="block text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink-3)]">
      {children}
    </span>
  );
}

const NOTE_TONE = {
  muted: { border: 'var(--cream-2)', background: 'var(--surface-mute)', ink: 'var(--ink-2)' },
  warning: { border: 'var(--gold)', background: 'var(--gold-tint)', ink: 'var(--ink-1)' },
  danger: {
    border: 'var(--terracotta)',
    background: 'var(--terracotta-tint)',
    ink: 'var(--ink-1)',
  },
} as const;

function Note({
  tone,
  children,
  className = '',
}: {
  tone: keyof typeof NOTE_TONE;
  children: React.ReactNode;
  className?: string;
}) {
  const palette = NOTE_TONE[tone];
  return (
    <p
      className={`rounded-[12px] border px-4 py-3 text-[12.5px] leading-relaxed ${className}`}
      style={{
        borderColor: palette.border,
        background: palette.background,
        color: palette.ink,
      }}
    >
      {children}
    </p>
  );
}

/** "4 minutes behind". Rounded, because a lag to the second reads as
 *  precision this number does not have. */
function behind(seconds: number | null): string {
  if (seconds === null) return 'never read';
  if (seconds < 90) return 'up to date';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${String(minutes)} minutes behind`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${String(hours)} hour${hours === 1 ? '' : 's'} behind`;
  return `${String(Math.round(hours / 24))} days behind`;
}

/** A date a person reads, for the day a deleted copy is erased for good. */
function onDate(iso: string): string {
  if (!iso) return 'the retention date';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function relative(iso: string | null): string {
  if (iso === null) return 'never';
  const seconds = (Date.now() - new Date(iso).getTime()) / 1000;
  if (seconds < 90) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${String(minutes)} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${String(hours)} hour${hours === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}
